import { useCallback, useEffect, useRef, useState } from 'react'
import { StreamEventType, ToolCall } from 'src/agent/types/streamingTypes'
import { store } from 'src/client'
import {
  ExtendThreadIDHeader,
  selectAuthedJsonAgentRequestConfig,
} from 'src/redux/selectors/axios.selectors'

export interface StreamMessage {
  id: string
  content?: string
  timestamp: Date
  type: 'thought' | 'tool' | 'tool_start' | 'result' | 'error' | 'done'
  eventType?: StreamEventType
  toolCalls?: ToolCall[]
}

export interface UseStreamingAgentChatOptions {
  threadId?: string
  onMessage?: (message: StreamMessage) => void
  onError?: (error: Error) => void
  onComplete?: () => void
}

export interface UseStreamingAgentChatReturn {
  messages: StreamMessage[]
  isConnected: boolean
  isStreaming: boolean
  error: Error | null
  sendPrompt: (prompt: string) => Promise<void>
  clearMessages: () => void
  threadId: string | null
}

/**
 * Hook for streaming agent chat using Server-Sent Events (SSE)
 * Connects to /api/v1/agent endpoint in muppeteer service
 */
export const useStreamingAgentChat = (
  options: UseStreamingAgentChatOptions = {},
): UseStreamingAgentChatReturn => {
  const [messages, setMessages] = useState<StreamMessage[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [threadId, setThreadId] = useState<string | null>(
    options.threadId || null,
  )

  const eventSourceRef = useRef<EventSource | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  // Use ref to track the latest threadId value to avoid stale closures
  const threadIdRef = useRef<string | null>(threadId)

  const addMessage = useCallback(
    (
      content: string | undefined,
      type: StreamMessage['type'],
      eventType?: StreamEventType,
      toolCalls?: ToolCall[],
    ) => {
      const message: StreamMessage = {
        id: `${Date.now()}-${Math.random()}`,
        content,
        timestamp: new Date(),
        type,
        eventType,
        toolCalls,
      }
      setMessages((prev) => [...prev, message])
      options.onMessage?.(message)
      return message
    },
    [options],
  )

  const clearMessages = useCallback(() => {
    setMessages([])
    setError(null)
  }, [])

  const closeConnection = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setIsConnected(false)
    setIsStreaming(false)
  }, [])

  const sendPrompt = useCallback(
    async (prompt: string) => {
      try {
        setError(null)
        setIsStreaming(true)

        // Get fresh auth config from store
        const state = store.getState()
        const config = selectAuthedJsonAgentRequestConfig(state)

        // Build SSE URL (baseURL already includes /api/v1)
        const url = new URL(`${config.baseURL}/agent`, window.location.origin)

        // Create abort controller for this request
        abortControllerRef.current = new AbortController()

        // Make POST request to initiate SSE stream
        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: {
            ...config.headers,
            'Content-Type': 'application/json',
            ...(threadIdRef.current
              ? { [ExtendThreadIDHeader]: threadIdRef.current }
              : {}),
          },
          body: JSON.stringify({ prompt }),
          signal: abortControllerRef.current.signal,
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        // Extract thread ID from response headers
        const responseThreadId = response.headers.get(ExtendThreadIDHeader)
        if (responseThreadId && responseThreadId !== threadIdRef.current) {
          setThreadId(responseThreadId)
          threadIdRef.current = responseThreadId
        }

        // Read SSE stream from response body
        const reader = response.body?.getReader()
        const decoder = new TextDecoder()

        if (!reader) {
          throw new Error('Response body is not readable')
        }

        setIsConnected(true)

        let currentEvent = ''
        let buffer = ''

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read()

          if (done) {
            break
          }

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')

          // Keep the last incomplete line in the buffer
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              // Parse event type
              currentEvent = line.slice(7).trim()
            } else if (line.startsWith('data: ')) {
              // Parse data
              const rawData = line.slice(6).trim()

              if (!rawData) continue

              // Handle based on event type
              switch (currentEvent) {
                case 'tool_start_event': {
                  try {
                    // Parse tool call data
                    const parsed = JSON.parse(rawData)
                    const toolCalls = parsed.tool_calls || []
                    addMessage(
                      undefined,
                      'tool_start',
                      StreamEventType.TOOL_START,
                      toolCalls,
                    )
                  } catch {
                    // Fallback to raw data if parsing fails
                    addMessage(rawData, 'tool_start')
                  }
                  break
                }

                case 'ai_event':
                case 'tool_event': {
                  try {
                    // Parse JSON response with markdown content
                    const parsed = JSON.parse(rawData)
                    const content = parsed.markdown || rawData
                    const eventType =
                      currentEvent === 'ai_event'
                        ? StreamEventType.AI_EVENT
                        : StreamEventType.TOOL_EVENT
                    addMessage(
                      content,
                      currentEvent === 'ai_event' ? 'thought' : 'tool',
                      eventType,
                    )
                  } catch {
                    // Fallback to raw data if not JSON
                    addMessage(
                      rawData,
                      currentEvent === 'ai_event' ? 'thought' : 'tool',
                    )
                  }
                  break
                }

                case 'done':
                  addMessage('Completed', 'done', StreamEventType.DONE)
                  options.onComplete?.()
                  setIsStreaming(false)
                  break

                case 'error': {
                  const err = new Error(rawData)
                  setError(err)
                  addMessage(rawData, 'error', StreamEventType.ERROR)
                  options.onError?.(err)
                  break
                }

                default:
                  // Fallback for messages without event type
                  try {
                    const parsed = JSON.parse(rawData)
                    const content = parsed.markdown || rawData
                    addMessage(content, 'thought')
                  } catch {
                    addMessage(rawData, 'thought')
                  }
              }
            }
          }

          currentEvent = ''
        }

        setIsStreaming(false)
        setIsConnected(false)
      } catch (err) {
        if (err instanceof Error) {
          if (err.name === 'AbortError') {
            // Request was cancelled, not an error
            return
          }
          setError(err)
          addMessage(err.message, 'error')
          options.onError?.(err)
        }
        setIsStreaming(false)
        setIsConnected(false)
      }
    },
    [addMessage, options],
  )

  // Sync threadId ref with state
  useEffect(() => {
    threadIdRef.current = threadId
  }, [threadId])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      closeConnection()
    }
  }, [closeConnection])

  return {
    messages,
    isConnected,
    isStreaming,
    error,
    sendPrompt,
    clearMessages,
    threadId,
  }
}
